/*
 * Outdoor Living collection filters.
 *
 * Progressive enhancement over a normal GET form: without JavaScript the
 * "Show N products" button submits the form and Shopify renders the page.
 * With JavaScript the products update as the customer ticks (desktop), and the
 * mobile sheet shows a live count on its button.
 *
 * No dependencies. One request at a time: a newer change cancels the older one.
 */
(function () {
  'use strict';

  var form = document.querySelector('[data-ocf-form]');
  if (!form) return;

  var sectionId = form.getAttribute('data-section-id') || '';
  var grid = document.getElementById('product-grid');
  var pagination = document.getElementById('ocf-pagination');
  var sortSelect = document.querySelector('[data-ocf-sort]');
  var openButton = document.querySelector('[data-ocf-open]');
  var closeButton = form.querySelector('[data-ocf-close]');
  var mobileQuery = window.matchMedia('(max-width: 991px)');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  var controller = null;
  var typingTimer = null;
  var lastFocus = null;
  var sheetOpen = false;

  function $(selector, root) { return (root || document).querySelector(selector); }
  function $$(selector, root) { return Array.prototype.slice.call((root || document).querySelectorAll(selector)); }

  /* ---------- sticky offset: sit just under the header the customer can see ---------- */
  /* The header keeps a full-height spacer inside .sticky-header and shrinks its visible bar as the page
     scrolls. Once compact, the spacer's box is taller than the bar, so measure the visible bar instead. */
  var stickyFrame = 0;
  function visibleHeaderBottom(header) {
    var bars = $$('.ol-hdr', header);
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].getClientRects().length) return bars[i].getBoundingClientRect().bottom;
    }
    return Math.max(header.getBoundingClientRect().bottom, header.offsetHeight);
  }
  function setStickyOffset() {
    var header = document.querySelector('.sticky-header');
    var top = 16;
    if (header) {
      top = Math.ceil(visibleHeaderBottom(header)) + 16;
      if (window.pageYOffset < 5) top = Math.ceil(header.offsetHeight) + 16;
    }
    var current = parseInt(document.documentElement.style.getPropertyValue('--ocf-top'), 10);
    if (current !== top) document.documentElement.style.setProperty('--ocf-top', top + 'px');
  }
  function queueStickyOffset() {
    if (stickyFrame) return;
    stickyFrame = window.requestAnimationFrame(function () { stickyFrame = 0; setStickyOffset(); });
  }
  setStickyOffset();
  window.addEventListener('resize', queueStickyOffset, { passive: true });
  window.addEventListener('scroll', queueStickyOffset, { passive: true });
  window.addEventListener('load', setStickyOffset);
  /* The header compacts inside a layout frame, so measure in the same frame the bar resizes. Re-observe after a section re-render. */
  var headerObserver = window.ResizeObserver ? new ResizeObserver(setStickyOffset) : null;
  function observeHeader() {
    var stickyHeader = document.querySelector('.sticky-header');
    if (!headerObserver || !stickyHeader) return;
    headerObserver.disconnect();
    headerObserver.observe(stickyHeader);
    $$('.ol-hdr', stickyHeader).forEach(function (bar) { headerObserver.observe(bar); });
  }
  observeHeader();
  document.addEventListener('shopify:section:load', function () {
    /* The header re-initialises on this same event. Measure after its scroll state is set up. */
    window.setTimeout(function () { observeHeader(); setStickyOffset(); }, 0);
  });

  /* ---------- query string from the form ---------- */
  function queryFromForm() {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (key === 'sort_by') return;
      if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
    });
    if (sortSelect && sortSelect.value && sortSelect.value !== sortSelect.getAttribute('data-default-sort')) {
      params.set('sort_by', sortSelect.value);
    }
    return params.toString();
  }

  /* Shopify can add section_id to the remove links when they come from a section response. Never keep it in the address. */
  function clean(query) {
    var params = new URLSearchParams(query || '');
    params.delete('section_id');
    return params.toString();
  }

  function pageUrl(query) {
    return window.location.pathname + (query ? '?' + query : '');
  }

  /* ---------- request and update ---------- */
  function update(query, pushHistory, force) {
    query = clean(query);
    if (controller) controller.abort();
    controller = new AbortController();
    var thisController = controller;

    if (grid) {
      grid.style.minHeight = grid.offsetHeight + 'px';
      grid.setAttribute('aria-busy', 'true');
    }
    form.setAttribute('data-ocf-busy', 'true');

    var url = window.location.pathname + '?' + (query ? query + '&' : '') + 'section_id=' + encodeURIComponent(sectionId);

    return fetch(url, { signal: thisController.signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Filter request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (thisController !== controller) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextGrid = doc.getElementById('product-grid');
        if (!nextGrid || !doc.querySelector('[data-ocf-form]')) throw new Error('Filter response had no product grid');

        applyResponse(doc, nextGrid, force, query);
        if (pushHistory) {
          window.history.pushState({ ocf: true }, '', pageUrl(query));
        }
        finishLoading(true);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        /* Anything unexpected: do a normal page load so the customer is never stuck. */
        window.location.assign(pageUrl(query));
      });
  }

  function finishLoading(ok) {
    if (grid) {
      grid.removeAttribute('aria-busy');
      grid.style.minHeight = '';
    }
    form.removeAttribute('data-ocf-busy');
    if (ok) controller = null;
    if (ok && !sheetOpen) keepGridInView();
  }

  function keepGridInView() {
    if (!grid) return;
    var rect = grid.getBoundingClientRect();
    var offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ocf-top'), 10) || 0;
    if (rect.top < offset) {
      window.scrollTo({ top: window.pageYOffset + rect.top - offset, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  }

  function applyResponse(doc, nextGrid, force, query) {
    /* The address we asked for is the truth about what is ticked. Never let a response untick anything. */
    var wanted = new URLSearchParams(query || '');
    var wantedCount = 0;
    wanted.forEach(function (value, key) { if (key.indexOf('filter.') === 0 && value !== '') wantedCount += 1; });
    var hasProducts = !!nextGrid.querySelector('a[href*="/products/"]');
    var responseKnowsFilters = !wantedCount || !!doc.querySelector('[data-ocf-active-n]:not([hidden])');

    /* Products and pagination */
    grid.replaceChildren.apply(grid, Array.prototype.slice.call(document.importNode(nextGrid, true).childNodes));
    var nextPagination = doc.getElementById('ocf-pagination');
    if (pagination) pagination.innerHTML = nextPagination ? nextPagination.innerHTML : '';

    /* Sort menu follows the address (back and forward buttons) */
    var nextSort = doc.querySelector('[data-ocf-sort]');
    if (sortSelect && nextSort) sortSelect.value = nextSort.value;

    if (responseKnowsFilters) {
      /* Count line, Filters button number, Clear all, chips, apply label */
      copyText('[data-ocf-count]', doc);
      copyText('[data-ocf-hero]', doc);
      copyText('[data-ocf-apply-label]', doc);
      copyText('[data-ocf-active-n]', doc);
      copyHidden('[data-ocf-active-n]', doc);
      copyHidden('[data-ocf-clear]', doc);
      copyHidden('[data-ocf-chips]', doc);
      var chips = $('[data-ocf-chips]');
      var nextChips = doc.querySelector('[data-ocf-chips]');
      if (chips && nextChips) chips.innerHTML = nextChips.innerHTML;
    } else {
      /* Shopify sent back a page with no filter state (nothing matches). Say so honestly and keep the ticks and Clear all. */
      var countLine = $('[data-ocf-count]');
      if (countLine) countLine.innerHTML = '<strong>0</strong> products match these filters';
      var activeN = $('[data-ocf-active-n]');
      if (activeN) { activeN.textContent = String(wantedCount); activeN.removeAttribute('hidden'); }
      var clearAll = $('[data-ocf-clear]');
      if (clearAll) clearAll.removeAttribute('hidden');
      var chipBox = $('[data-ocf-chips]');
      if (chipBox) { chipBox.innerHTML = ''; chipBox.setAttribute('hidden', ''); }
      var applyLabel = $('[data-ocf-apply-label]');
      if (applyLabel) applyLabel.textContent = 'Show 0 products';
    }
    void hasProducts;
    /* Links copied from a section response can carry section_id: remove it so a new tab opens the real page. */
    $$('a[data-ocf-remove], a[data-ocf-clear]').forEach(function (link) {
      var href = new URL(link.getAttribute('href'), window.location.href);
      if (href.searchParams.has('section_id')) {
        href.searchParams.delete('section_id');
        link.setAttribute('href', href.pathname + href.search);
      }
    });

    /* Every input: keep the same elements (so focus stays) but refresh state */
    var nextForm = doc.querySelector('[data-ocf-form]');
    var nextGroups = {};
    $$('[data-ocf-group]', nextForm).forEach(function (candidate) {
      var title = $('.ocf-group__name', candidate);
      if (title) nextGroups[title.textContent.trim()] = candidate;
    });
    $$('[data-ocf-group]', form).forEach(function (group) {
      var title = $('.ocf-group__name', group);
      var nextGroup = responseKnowsFilters && title ? nextGroups[title.textContent.trim()] : null;
      if (nextGroup) {
        var now = $('[data-ocf-now]', group);
        var nextNow = $('[data-ocf-now]', nextGroup);
        if (now && nextNow) now.textContent = nextNow.textContent.trim();
      }

      /* A page that loaded already filtered only has the options that matched then. If the answer brings options back, rebuild this group so nothing is missing. */
      if (nextGroup && group.getAttribute('data-ocf-kind') !== 'price') {
        var haveKeys = {};
        $$('input[type=checkbox]', group).forEach(function (c) { haveKeys[c.name + '\u0001' + c.value] = true; });
        var missing = $$('input[type=checkbox]', nextGroup).some(function (c) { return !haveKeys[c.name + '\u0001' + c.value]; });
        var body = $('.ocf-group__body', group);
        var nextBody = $('.ocf-group__body', nextGroup);
        if (missing && body && nextBody) {
          var focusKey = document.activeElement && document.activeElement.name ? document.activeElement.name + '\u0001' + document.activeElement.value : '';
          body.innerHTML = nextBody.innerHTML;
          $$('input[type=checkbox]', group).forEach(function (c) {
            c.checked = wanted.getAll(c.name).indexOf(c.value) !== -1;
            if (c.checked) c.disabled = false;
            if (focusKey && c.name + '\u0001' + c.value === focusKey) c.focus({ preventScroll: true });
          });
          $$('img[data-ocf-logo]', group).forEach(function (img) {
            if (img.complete && img.naturalWidth === 0) logoFallback(img);
            else img.addEventListener('error', function () { logoFallback(img); });
          });
          return;
        }
      }

      var byKey = {};
      if (nextGroup) {
        $$('input', nextGroup).forEach(function (candidate) {
          byKey[candidate.name + '\u0001' + (candidate.type === 'checkbox' ? candidate.value : '')] = candidate;
        });
      }
      $$('input', group).forEach(function (input) {
        if (input.hasAttribute('data-ocf-skip')) return;
        if (input.type === 'checkbox') {
          input.checked = wanted.getAll(input.name).indexOf(input.value) !== -1;
          var nextInput = byKey[input.name + '\u0001' + input.value];
          if (!nextInput) {
            /* Shopify leaves out any option that no longer matches. Grey it out so it cannot lead to an empty page. */
            if (responseKnowsFilters && !input.checked) {
              input.disabled = true;
              var goneLabel = input.closest('label');
              var goneCount = goneLabel && $('[data-ocf-count-of]', goneLabel);
              if (goneCount) goneCount.textContent = '0';
              var goneMeter = goneLabel && $('[data-ocf-meter]', goneLabel);
              if (goneMeter) goneMeter.setAttribute('style', '');
            }
            return;
          }
          input.disabled = input.checked ? false : nextInput.disabled;
          var label = input.closest('label');
          var count = label && $('[data-ocf-count-of]', label);
          var nextLabel = nextInput.closest('label');
          var nextCount = nextLabel && $('[data-ocf-count-of]', nextLabel);
          if (count && nextCount) count.textContent = nextCount.textContent;
          var meter = label && $('[data-ocf-meter]', label);
          var nextMeter = nextLabel && $('[data-ocf-meter]', nextLabel);
          if (meter && nextMeter) meter.setAttribute('style', nextMeter.getAttribute('style') || '');
        } else if ((force || document.activeElement !== input) && input.name && input.name.indexOf('filter.') === 0) {
          input.value = wanted.get(input.name) || '';
        }
      });
    });
    syncPrice();
    refreshExtras();
  }

  function copyText(selector, doc) {
    var current = $(selector);
    var next = doc.querySelector(selector);
    if (current && next) current.innerHTML = next.innerHTML;
  }

  function copyHidden(selector, doc) {
    var current = $(selector);
    var next = doc.querySelector(selector);
    if (!current || !next) return;
    if (next.hasAttribute('hidden')) current.setAttribute('hidden', '');
    else current.removeAttribute('hidden');
  }

  /* ---------- events ---------- */
  function changeNow() {
    window.clearTimeout(typingTimer);
    update(queryFromForm(), true);
  }

  form.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement) || target.hasAttribute('data-ocf-search')) return;
    changeNow();
  });

  /* Typing a price: wait until they pause. */
  form.addEventListener('input', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-ocf-price')) return;
    window.clearTimeout(typingTimer);
    typingTimer = window.setTimeout(changeNow, 700);
  });

  /* Enter in a price box, or the apply button. */
  form.addEventListener('submit', function (event) {
    event.preventDefault();
    window.clearTimeout(typingTimer);
    var query = queryFromForm();
    var wasPending = !!controller;
    if (sheetOpen) closeSheet();
    if (query !== window.location.search.replace(/^\?/, '') || wasPending) {
      update(query, true);
    }
  });

  if (sortSelect) {
    sortSelect.addEventListener('change', function () { changeNow(); });
  }

  /* Links that go to a filtered address: remove chip, clear all. */
  form.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[data-ocf-remove], a[data-ocf-clear]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    var next = new URL(link.href, window.location.href);
    var params = new URLSearchParams(next.search);
    if (sortSelect && sortSelect.value !== sortSelect.getAttribute('data-default-sort')) params.set('sort_by', sortSelect.value);
    update(params.toString(), true, true);
  });

  /* Back and forward buttons. */
  window.addEventListener('popstate', function () {
    update(window.location.search.replace(/^\?/, ''), false, true);
  });


  /* ---------- show more / fewer, and search ---------- */
  function refreshExtras() {
    $$('[data-ocf-group]', form).forEach(function (group) {
      if (group.hasAttribute('data-ocf-expanded')) return;
      if ($('[data-ocf-extra] input:checked', group)) setExpanded(group, true);
    });
  }

  function setExpanded(group, open) {
    var button = $('[data-ocf-expand]', group);
    if (open) group.setAttribute('data-ocf-expanded', '');
    else group.removeAttribute('data-ocf-expanded');
    if (button) {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      button.textContent = open ? button.getAttribute('data-less') : button.getAttribute('data-more');
    }
  }

  form.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ocf-expand]');
    if (!button) return;
    var group = button.closest('[data-ocf-group]');
    setExpanded(group, !group.hasAttribute('data-ocf-expanded'));
  });

  form.addEventListener('input', function (event) {
    var box = event.target;
    if (!box || !box.hasAttribute || !box.hasAttribute('data-ocf-search')) return;
    var group = box.closest('[data-ocf-group]');
    var q = box.value.trim().toLowerCase();
    var any = false;
    $$('[data-ocf-name]', group).forEach(function (item) {
      var show = !q || item.getAttribute('data-ocf-name').indexOf(q) !== -1;
      item.classList.toggle('ocf-nomatch', !show);
      if (show) any = true;
    });
    /* While searching, look through every value, not only the first few. */
    if (q) group.setAttribute('data-ocf-expanded', '');
    else if (!group.querySelector('[data-ocf-expand][aria-expanded="true"]')) group.removeAttribute('data-ocf-expanded');
    var empty = $('.ocf-find__none', group);
    if (!any && q) {
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'ocf-hint ocf-find__none';
        box.parentNode.insertBefore(empty, box.nextSibling);
      }
      empty.textContent = 'Nothing matches that.';
    } else if (empty) {
      empty.parentNode.removeChild(empty);
    }
  });

  form.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.hasAttribute && event.target.hasAttribute('data-ocf-search')) event.preventDefault();
  });

  /* A logo that fails to load becomes the brand name, so a tile is never empty. */
  function logoFallback(img) {
    var name = document.createElement('span');
    name.className = 'ocf-tile__name';
    name.textContent = img.getAttribute('alt') || '';
    if (img.parentNode) img.parentNode.replaceChild(name, img);
  }
  $$('img[data-ocf-logo]', form).forEach(function (img) {
    if (img.complete && img.naturalWidth === 0) logoFallback(img);
    else img.addEventListener('error', function () { logoFallback(img); });
  });
  refreshExtras();

  /* ---------- price: histogram and two handles ---------- */
  var hist = $('[data-ocf-hist]', form);
  var rLo = hist && $('[data-ocf-rlo]', hist);
  var rHi = hist && $('[data-ocf-rhi]', hist);
  var boxLo = $('[data-ocf-plo]', form);
  var boxHi = $('[data-ocf-phi]', form);
  var barsEl = hist && $('[data-ocf-bars]', hist);
  var fillEl = hist && $('[data-ocf-fill]', hist);
  var lowest = 0;
  var highest = 0;
  var prices = [];
  var trueMin = null;
  var snap = 1;
  var BINS = 12;

  function num(value, fallback) {
    var n = parseInt(String(value).replace(/[^0-9]/g, ''), 10);
    return isNaN(n) ? fallback : n;
  }

  function stepFor(max) {
    if (max > 4000) return 50;
    if (max > 800) return 10;
    return 1;
  }

  function setupPrice() {
    if (!hist || !rLo || !rHi || !boxLo || !boxHi) return;
    highest = num(hist.getAttribute('data-max'), 0);
    if (highest <= 0) return;
    snap = stepFor(highest);
    lowest = 0;
    var known = prices.slice();
    if (trueMin !== null) known.push(trueMin);
    if (known.length) {
      lowest = Math.max(0, Math.floor(Math.min.apply(null, known)));
      if (lowest >= highest) lowest = 0;
    }
    [rLo, rHi].forEach(function (r) {
      r.min = lowest;
      r.max = highest;
      r.step = 1;
    });
    boxHi.setAttribute('placeholder', String(highest));
    boxLo.setAttribute('placeholder', String(lowest));
    hist.setAttribute('data-ocf-ready', '');
    syncPrice();
  }

  function drawBars(lo, hi) {
    if (!barsEl) return;
    var span = highest - lowest;
    if (!prices.length || span <= 0) { barsEl.innerHTML = ''; return; }
    var counts = [];
    for (var i = 0; i < BINS; i++) counts.push(0);
    prices.forEach(function (price) {
      var index = Math.min(BINS - 1, Math.max(0, Math.floor((price - lowest) / span * BINS)));
      counts[index]++;
    });
    var top = Math.max.apply(null, counts) || 1;
    if (barsEl.children.length !== BINS) {
      barsEl.innerHTML = '';
      for (var j = 0; j < BINS; j++) barsEl.appendChild(document.createElement('span')).className = 'ocf-hist__bar';
    }
    for (var k = 0; k < BINS; k++) {
      var bar = barsEl.children[k];
      var from = lowest + span * k / BINS;
      var to = lowest + span * (k + 1) / BINS;
      bar.style.height = (counts[k] ? Math.max(6, counts[k] / top * 100) : 0) + '%';
      if (to > lo && from < hi) bar.setAttribute('data-in', '');
      else bar.removeAttribute('data-in');
    }
  }

  /* Boxes to handles and bars. */
  function syncPrice() {
    if (!hist || !hist.hasAttribute('data-ocf-ready')) return;
    var lo = num(boxLo.value, lowest);
    var hi = num(boxHi.value, highest);
    lo = Math.min(Math.max(lo, lowest), highest);
    hi = Math.min(Math.max(hi, lowest), highest);
    if (lo > hi) { var t = lo; lo = hi; hi = t; }
    rLo.value = lo;
    rHi.value = hi;
    paintPrice(lo, hi);
  }

  function paintPrice(lo, hi) {
    var span = highest - lowest || 1;
    fillEl.style.left = ((lo - lowest) / span * 100) + '%';
    fillEl.style.right = (100 - (hi - lowest) / span * 100) + '%';
    drawBars(lo, hi);
  }

  /* Handles to boxes. A handle at its end means no limit, so its box stays empty. */
  /* Handles move in tidy steps, but the two ends are always the real lowest and highest price. */
  function snapTo(value) {
    if (value <= lowest + snap / 2) return lowest;
    if (value >= highest - snap / 2) return highest;
    return Math.min(highest, Math.max(lowest, Math.round(value / snap) * snap));
  }

  function fromHandles(which) {
    var step = snap;
    var lo = snapTo(num(rLo.value, lowest));
    var hi = snapTo(num(rHi.value, highest));
    if (which === 'lo') rLo.value = lo;
    if (which === 'hi') rHi.value = hi;
    if (which === 'lo' && lo > hi - step) { lo = Math.max(lowest, hi - step); rLo.value = lo; }
    if (which === 'hi' && hi < lo + step) { hi = Math.min(highest, lo + step); rHi.value = hi; }
    boxLo.value = lo <= lowest ? '' : String(lo);
    boxHi.value = hi >= highest ? '' : String(hi);
    paintPrice(lo, hi);
  }

  if (hist && rLo && rHi) {
    rLo.addEventListener('input', function () { fromHandles('lo'); });
    rHi.addEventListener('input', function () { fromHandles('hi'); });
    [boxLo, boxHi].forEach(function (box) { box.addEventListener('input', syncPrice); });
    /* A handle that sits on top of the other one must stay reachable. */
    rLo.addEventListener('pointerdown', function () { rLo.style.zIndex = 2; rHi.style.zIndex = 1; });
    rHi.addEventListener('pointerdown', function () { rHi.style.zIndex = 2; rLo.style.zIndex = 1; });

    setupPrice();

    var url = hist.getAttribute('data-url');
    var cacheKey = 'ocf-prices:' + url;
    var cached = null;
    try { cached = JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null'); } catch (e) { cached = null; }
    if (cached && cached.length) {
      prices = cached;
      setupPrice();
    } else if (url) {
      fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : { products: [] }; })
        .then(function (data) {
          var list = [];
          (data.products || []).forEach(function (product) {
            var low = null;
            (product.variants || []).forEach(function (variant) {
              var price = parseFloat(variant.price);
              if (price > 0 && (low === null || price < low)) low = price;
            });
            if (low !== null) list.push(Math.floor(low));
          });
          if (!list.length) return;
          prices = list;
          try { window.sessionStorage.setItem(cacheKey, JSON.stringify(list)); } catch (e) { /* private mode */ }
          setupPrice();
        })
        .catch(function () { /* no histogram, the boxes still work */ });
    }

    /* The cheapest product in the whole collection, so the From box shows the real lowest price. */
    if (url) {
      var minKey = cacheKey + ':min';
      var cachedMin = null;
      try { cachedMin = parseFloat(window.sessionStorage.getItem(minKey)); } catch (e) { cachedMin = null; }
      if (cachedMin && !isNaN(cachedMin)) {
        trueMin = cachedMin;
        setupPrice();
      } else {
        fetch(url.replace('limit=250', 'limit=1') + '&sort_by=price-ascending', { credentials: 'same-origin' })
          .then(function (response) { return response.ok ? response.json() : { products: [] }; })
          .then(function (data) {
            var low = null;
            ((data.products && data.products[0] && data.products[0].variants) || []).forEach(function (variant) {
              var price = parseFloat(variant.price);
              if (price > 0 && (low === null || price < low)) low = price;
            });
            if (low === null) return;
            trueMin = low;
            try { window.sessionStorage.setItem(minKey, String(low)); } catch (e) { /* private mode */ }
            setupPrice();
          })
          .catch(function () { /* the other prices still give a close figure */ });
      }
    }
  }

  /* ---------- mobile sheet ---------- */
  function focusables() {
    return $$('a[href], button:not([disabled]), input:not([disabled]), select, summary', form)
      .filter(function (el) { return el.offsetParent !== null && !el.hasAttribute('hidden'); });
  }

  function openSheet() {
    if (!mobileQuery.matches || sheetOpen) return;
    sheetOpen = true;
    lastFocus = document.activeElement;
    form.setAttribute('data-open', 'true');
    form.setAttribute('role', 'dialog');
    form.setAttribute('aria-modal', 'true');
    document.documentElement.classList.add('ocf-lock');
    if (openButton) openButton.setAttribute('aria-expanded', 'true');
    if (closeButton) closeButton.focus();
  }

  function closeSheet() {
    if (!sheetOpen) return;
    sheetOpen = false;
    form.removeAttribute('data-open');
    form.setAttribute('role', 'search');
    form.removeAttribute('aria-modal');
    document.documentElement.classList.remove('ocf-lock');
    if (openButton) {
      openButton.setAttribute('aria-expanded', 'false');
      openButton.focus();
    } else if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
    }
    window.setTimeout(keepGridInView, 0);
  }

  if (openButton) openButton.addEventListener('click', openSheet);
  if (closeButton) closeButton.addEventListener('click', closeSheet);

  document.addEventListener('keydown', function (event) {
    if (!sheetOpen) return;
    if (event.key === 'Escape') { closeSheet(); return; }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  function onBreakpointChange() {
    if (!mobileQuery.matches && sheetOpen) closeSheet();
    collapseForPhone();
  }
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onBreakpointChange);
  else if (mobileQuery.addListener) mobileQuery.addListener(onBreakpointChange);

  /* On a phone the sheet is a short list: open only groups that have a choice. */
  function collapseForPhone() {
    if (!mobileQuery.matches || sheetOpen) return;
    /* Every group stays open; the customer can fold any of them. */
  }
  collapseForPhone();
})();
